// Archetype quiz content — questions + result copy (combined female/male spec).
// Families: female A=Queen B=Sage C=Lover D=Seeker · male A=King B=Sage C=Lover D=Rebel

export const PATHS = {
  female: { label: 'Feminine archetypes', emoji: '🌙' },
  male: { label: 'Masculine archetypes', emoji: '⚔️' }
};

/** 12 questions per path; options in A/B/C/D order. */
export const QUESTIONS = {
  female: [
    { title: 'Faced with conflict, you…', options: ['Take charge', 'Analyze quietly', 'Feel it deeply', 'Challenge it'] },
    { title: 'Your natural role in a group', options: ['Leader', 'Advisor', 'Peacemaker', 'Visionary'] },
    { title: 'Your biggest strength', options: ['Authority', 'Insight', 'Empathy', 'Independence'] },
    { title: 'Your inner voice usually says…', options: ['“Handle it.”', '“Think deeper.”', '“Feel into this.”', '“Break the rule.”'] },
    { title: 'You feel most alive when…', options: ['Leading', 'Learning', 'Loving', 'Exploring'] },
    { title: 'Your fear', options: ['Losing control', 'Being misunderstood', 'Being unloved', 'Being trapped'] },
    { title: 'When stressed, you…', options: ['Tighten control', 'Overthink', 'Seek reassurance', 'Withdraw'] },
    { title: 'You value most', options: ['Respect', 'Truth', 'Connection', 'Authenticity'] },
    { title: 'Your hidden side', options: ['Softness', 'Sensitivity', 'Jealousy', 'Anger'] },
    { title: 'Your shadow shows up as…', options: ['Control', 'Overthinking', 'Emotional dependency', 'Rebellion'] },
    { title: 'Deep down, you want to…', options: ['Be respected', 'Be understood', 'Be cherished', 'Be free'] },
    {
      title: 'Looking back on your life, you want to have…',
      options: [
        'Built something lasting',
        'Understood life deeply',
        'Loved and been loved',
        'Lived entirely on your own terms'
      ]
    }
  ],
  male: [
    {
      title: 'Something goes wrong in a group — you…',
      options: [
        'Take charge of fixing it',
        'Work out why it happened',
        'Check how everyone’s doing',
        'Question the plan itself'
      ]
    },
    {
      title: 'Your natural role among friends',
      options: ['The reliable one', 'The advisor', 'The one who keeps everyone close', 'The wildcard']
    },
    { title: 'Your biggest strength', options: ['Steadiness', 'Insight', 'Depth', 'Independence'] },
    { title: 'Your inner voice usually says…', options: ['“It’s on me.”', '“Think it through.”', '“Stay close.”', '“Says who?”'] },
    { title: 'You feel most alive when…', options: ['Leading', 'Mastering something', 'Deeply connected', 'Completely free'] },
    {
      title: 'Your deepest fear',
      options: ['Letting everyone down', 'Being wrong about what matters', 'Being left', 'Being trapped']
    },
    {
      title: 'Under real stress, you…',
      options: ['Tighten control', 'Retreat into your head', 'Reach for people you trust', 'Pull away from everyone']
    },
    { title: 'You value most', options: ['Respect', 'Truth', 'Closeness', 'Authenticity'] },
    {
      title: 'Your hidden side',
      options: ['Exhaustion you don’t show', 'Feelings you’ve never said out loud', 'Jealousy', 'Loneliness']
    },
    {
      title: 'Your shadow shows up as…',
      options: ['Control', 'Overthinking', 'Losing yourself in someone', 'Walls no one gets past']
    },
    { title: 'Deep down, you want to be…', options: ['Respected', 'Understood', 'Truly known', 'Free'] },
    {
      title: 'The life you actually want includes…',
      options: [
        'Something lasting you built',
        'Real mastery of your craft',
        'People who truly know you',
        'Freedom nobody can revoke'
      ]
    }
  ]
};

/** Result copy per path per family: name / mirror line / gift. */
export const RESULTS = {
  female: {
    A: { name: 'The Queen', mirror: 'You embody authority.', gift: 'Leadership, stability, and sovereignty: you create order other people can rely on.' },
    B: { name: 'The Sage', mirror: 'You seek truth.', gift: 'Wisdom and knowledge: you understand things deeply before you act on them.' },
    C: { name: 'The Lover', mirror: 'You feel life deeply.', gift: 'Passion and connection: you love deeply and make people feel truly seen.' },
    D: { name: 'The Seeker', mirror: 'You evolve constantly.', gift: 'Growth and expansion: you’re always reaching for the next version of yourself.' }
  },
  male: {
    A: { name: 'The King', mirror: 'You hold the center.', gift: 'Order, responsibility, and stewardship: you build stability other people can stand on.' },
    B: { name: 'The Sage', mirror: 'You see beneath the surface.', gift: 'Wisdom and perspective: you see the pattern under the noise before anyone else does.' },
    C: { name: 'The Lover', mirror: 'You feel life at full depth.', gift: 'Depth and devotion: you make the people you love feel genuinely seen.' },
    D: { name: 'The Rebel', mirror: 'You answer to yourself.', gift: 'Autonomy and honesty: you say the true thing out loud and live on your own terms.' }
  }
};

/** Result emblem per path per family (shown in the reveal medallion). */
export const EMBLEMS = {
  female: { A: '👑', B: '🦉', C: '🌹', D: '🧭' },
  male: { A: '🦁', B: '🦉', C: '🌹', D: '🔥' }
};

export const STORE_LINKS = {
  ios: 'https://apps.apple.com/app/id6746166910?ct=archetype-quiz',
  android:
    'https://play.google.com/store/apps/details?id=com.affirmation.mindlift&referrer=utm_source%3Dweb%26utm_medium%3Dfunnel%26utm_campaign%3Darchetype_quiz'
};

/** Share spec: canonical slug per path+family, for generated pages and share URLs. */
export const ARCHETYPE_SLUGS = {
  female: { A: 'queen', B: 'sage', C: 'lover', D: 'seeker' },
  male: { A: 'king', B: 'sage', C: 'lover', D: 'rebel' }
};

/** Share spec: one entry per unique slug (sage/lover are shared between paths). */
export const ARCHETYPES = {
  queen: {
    name: 'The Queen',
    emblem: '👑',
    family: 'A',
    tagline: 'You create order other people can rely on.',
    shadow: 'Control. When things feel uncertain, you tighten your grip and carry more than is yours to carry.',
    illo: 'arch-queen',
    longDescription: [
      "Leading rarely feels optional when you're a Queen. Someone has to hold the room together, make the call, keep things from sliding — and it's usually you. That instinct for order isn't ambition for its own sake; it's how you show up for the people who depend on you, especially when nobody else steps forward first. Other people notice it as calm authority, even when it costs you something to hold that steadiness together.",
      "The shadow side is quieter than the gift: an unspoken rule that if you don't handle it, no one will, so you keep tightening your grip on things that were never fully yours to carry. That grip can look like control from the outside, even though underneath it's closer to fear. You don't need to stop leading — you need a way to notice when you're carrying more than the moment actually requires. That's the small, practical shift MindLift trains for."
    ]
  },
  king: {
    name: 'The King',
    emblem: '🦁',
    family: 'A',
    tagline: 'You build stability other people can stand on.',
    shadow: "Control and exhaustion you don't show. The fear of letting everyone down keeps you from letting anyone in.",
    illo: 'arch-king',
    longDescription: [
      "Being the reliable one isn't a role you auditioned for — it's just where you land when things get hard. You build the stability other people quietly lean on, and you do it without asking for credit, because the responsibility feels less like a burden and more like who you are. People trust you precisely because you rarely let the strain show, and that trust is earned one hard day at a time.",
      "What doesn't show is the exhaustion underneath — the fear that letting anyone down means you've failed at the one job that matters, so you keep people slightly at arm's length instead of risking it. Over time that distance can start to feel like the price of being dependable, even when it isn't. Real steadiness sometimes looks like letting someone else catch part of the weight instead of carrying all of it invisibly. You don't have to carry that alone to still be dependable. MindLift trains the pause where you let someone else hold a corner of it too."
    ]
  },
  sage: {
    name: 'The Sage',
    emblem: '🦉',
    family: 'B',
    tagline: 'You see the pattern under the noise.',
    shadow: 'Overthinking. Under stress you retreat into your head, analyse instead of act, and fear being misunderstood.',
    illo: 'arch-sage',
    longDescription: [
      "You'd rather understand something fully than act on it half-formed. That instinct — to slow down, gather the whole picture, see the pattern underneath the noise — is real insight, not indecision. People come to you because you notice what everyone else missed, and because you're rarely the one reacting first without thinking it through. That habit is part of why people trust your judgment.",
      "The cost is that understanding can become a hiding place: one more angle to consider before you'll trust your own read, one more loop of analysis instead of the harder step of speaking or acting. Under real stress, that retreat into your head can start to feel safer than being misunderstood out loud. Staying silent until every angle feels settled seems safe, but it has its own cost too. Your thinking was never the problem — the retreat into it under stress is. MindLift trains the moment where you close the loop and act on what you already know."
    ]
  },
  lover: {
    name: 'The Lover',
    emblem: '🌹',
    family: 'C',
    tagline: 'You make people feel genuinely seen.',
    shadow: "Emotional dependency. When you're afraid of being left, you lose yourself in someone else or seek reassurance you can't quite trust.",
    illo: 'arch-lover',
    longDescription: [
      "You feel things at full volume, and you make the people around you feel genuinely seen — really seen, not just noticed. That depth is rare, and it's exactly why your relationships tend to run deeper than most people's, even when depth like that isn't always comfortable to carry. People who know you well say ordinary moments feel more significant with you fully present in them.",
      "The same depth that lets you love fully can turn against you when you're afraid of losing someone: you start reading every silence as a sign, chasing reassurance that never quite lands, or losing your own shape inside someone else's. Wanting closeness that badly isn't weakness — it's what happens when feeling everything meets the fear of being left. Reassurance from someone else was never going to do what steadier reassurance from yourself can. MindLift trains the pause between the fear and the reach, so connection stays a choice instead of a scramble."
    ]
  },
  seeker: {
    name: 'The Seeker',
    emblem: '🧭',
    family: 'D',
    tagline: "You're always reaching for the next version of yourself.",
    shadow: 'Withdrawal. When you feel trapped, you pull away, restart, and avoid committing to anything long enough for it to grow.',
    illo: 'arch-seeker',
    longDescription: [
      "Staying still has never suited you. You're drawn to the next version of yourself — a new city, a new skill, a different way of living — and that restlessness has probably taken you further than staying put ever would have. Growth, for you, isn't a phase; it's closer to a default setting. Other people sometimes read that as flightiness, but it's closer to refusing a life that doesn't fit.",
      "But the same pull that drives growth can become an exit when things get hard: you feel trapped, so you pull away, restart, or leave before anything has the chance to actually take root. From the outside it can look like freedom, but underneath it's often just avoidance wearing freedom's clothes. Learning to tell a genuine dead end apart from ordinary discomfort matters more than the running itself. Wanting more isn't the problem. MindLift trains the moment where you can stay with something uncomfortable long enough to see if it was actually working."
    ]
  },
  rebel: {
    name: 'The Rebel',
    emblem: '🔥',
    family: 'D',
    tagline: 'You say the true thing out loud.',
    shadow: 'Walls. When you feel controlled, you push everyone away and mistake loneliness for freedom.',
    illo: 'arch-rebel',
    longDescription: [
      "You say the true thing out loud, even when it costs you something, and you'd rather live on your own terms than borrow someone else's version of a good life. That honesty is rare, and the people who know you actually trust it, because they know you won't tell them what they want to hear instead. That refusal to perform is exactly why the people who get close to you trust what they're getting.",
      "The shadow shows up as walls: when you feel controlled or boxed in, you push everyone back and call the resulting distance freedom, even when it's closer to loneliness. It's a hard pattern to see from inside it, because pushing away can genuinely feel like protecting what's yours. Letting someone stay close doesn't have to mean giving up the terms you've fought to set for your own life. Independence and connection aren't actually opposites. MindLift trains the pause where you can let someone stay close without it costing you your own ground."
    ]
  }
};

/** Share spec §5: ten family-pair lines, keyed by the two families sorted alphabetically. {A}/{D} are template slots filled with the actual names (Queen/King, Seeker/Rebel) at render time. */
export const PAIRS = {
  AA: "Two sovereigns. You both carry the room — decide who carries what, or you'll compete for the same weight.",
  AB: 'One decides, one deliberates. Let the Sage slow the call down before the {A} makes it.',
  AC: 'Order meets warmth. The {A} protects, the Lover softens — trouble starts when protection reads as control.',
  AD: "Structure meets freedom. You'll clash on rules and rescue each other from your extremes.",
  BB: 'Two Sages. Endless understanding, slow action — someone has to say "enough thinking, go."',
  BC: 'You think it through, they feel it first. Each has what the other skips.',
  BD: 'You need a reason, they need room. Give each other both.',
  CC: 'Two Lovers. Everything is felt, nothing is filed — beautiful, and exhausting without an anchor.',
  CD: 'Closeness meets freedom. The oldest tension there is; name it early.',
  DD: "Two free spirits. Nobody's trapped and nobody's holding on — check that's what you both want."
};

/** Share spec §4: the complement guess is the family the person scored lowest in, one hop across the wheel. */
export const COMPLEMENT = { A: 'D', B: 'C', C: 'B', D: 'A' };
